import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, AlertTriangle, Plus, RotateCcw, Trash2, Save } from "lucide-react";

export default function RefundPolicy() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("refpol.meta_title"));
  const [tiers, setTiers] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [samplePrice, setSamplePrice] = useState("100");
  const [sampleTier, setSampleTier] = useState(0);
  const [estimate, setEstimate] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/refund-policy");
      setTiers(data?.tiers || []);
      setDefaults(data?.platform_default || []);
      setNotes(data?.notes || "");
    } catch { setTiers([]); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  useEffect(() => {
    if (!tiers || !tiers[sampleTier]) { setEstimate(null); return; }
    const price = Math.max(0, Math.round(Number(String(samplePrice).replace(",", ".") || "0") * 100));
    if (!price) { setEstimate(null); return; }
    const pct = tiers[sampleTier].refund_pct;
    api.get(`/coach/refund-cost-estimate?price_cents=${price}&refund_pct=${pct}`)
      .then(({ data }) => setEstimate(data)).catch(() => setEstimate(null));
  }, [tiers, sampleTier, samplePrice]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const updateTier = (idx, field, val) => setTiers((prev) => prev.map((tr, i) => i === idx ? {
    ...tr,
    [field]: field === "hours_before" ? Math.max(0, Math.round(Number(val || "0"))) : Math.max(0, Math.min(100, Number(val || "0"))),
  } : tr));
  const addTier = () => setTiers((prev) => [{ hours_before: (prev[0]?.hours_before ?? 72) + 24, refund_pct: 100 }, ...prev]);
  const removeTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));
  const reset = () => setTiers([...defaults]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/coach/refund-policy", { tiers, notes }); toast.success(t("refpol.saved")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="coach-refund-policy-page">
      <Seo title={t("refpol.seo")} noindex />
      <Link to="/app/coach/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="refpol-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.title")}
      </Link>
      <PageHeader eyebrow={t("refpol.eyebrow")} title={t("refpol.title")} subtitle={t("refpol.subtitle")} />

      <div className="flex items-start gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-4">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-sm text-amber-800"><strong>{t("refpol.warn_bold")}</strong> {t("refpol.warn_body")}</p>
      </div>

      {tiers === null ? (
        <div className="mt-5 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : (
        <div className="mt-5 space-y-5">
          <div>
            <p className="font-display text-lg text-foreground">{t("refpol.your_tiers")}</p>
            <p className="mb-3 text-xs text-muted-foreground">{t("refpol.tiers_hint")}</p>
            <div className="space-y-2">
              {tiers.map((tr, idx) => (
                <div key={idx} className="flex items-end gap-2 rounded-xl border border-border bg-card p-3" data-testid={`refpol-tier-${idx}`}>
                  <div className="flex-1">
                    <label className="mb-1 block text-xs text-muted-foreground">{t("refpol.hours_before")}</label>
                    <Input type="number" value={tr.hours_before} onChange={(e) => updateTier(idx, "hours_before", e.target.value)} data-testid={`refpol-hours-${idx}`} />
                  </div>
                  <div className="flex-1">
                    <label className="mb-1 block text-xs text-muted-foreground">{t("refpol.refund_pct")}</label>
                    <Input type="number" value={tr.refund_pct} onChange={(e) => updateTier(idx, "refund_pct", e.target.value)} data-testid={`refpol-pct-${idx}`} />
                  </div>
                  <Button size="icon" variant="ghost" className="h-10 w-10 rounded-full text-rose-600" onClick={() => removeTier(idx)} data-testid={`refpol-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={addTier} data-testid="refpol-add"><Plus className="h-3.5 w-3.5" /> {t("refpol.add_tier")}</Button>
              <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={reset} data-testid="refpol-reset"><RotateCcw className="h-3.5 w-3.5" /> {t("refpol.reset")}</Button>
            </div>
          </div>

          {/* Cost estimator */}
          <div>
            <p className="font-display text-lg text-foreground">{t("refpol.cost_title")}</p>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <div className="w-28">
                <label className="mb-1 block text-xs text-muted-foreground">{t("refpol.sample_price")}</label>
                <Input type="text" inputMode="decimal" value={samplePrice} onChange={(e) => setSamplePrice(e.target.value)} data-testid="refpol-sample-price" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {tiers.map((tr, idx) => (
                  <button key={idx} onClick={() => setSampleTier(idx)} data-testid={`refpol-est-tier-${idx}`}
                    className={`rounded-full border px-2.5 py-1.5 text-xs ${sampleTier === idx ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                    ≥{tr.hours_before}h → {tr.refund_pct}%
                  </button>
                ))}
              </div>
            </div>
            {estimate && (
              <div className="mt-3 space-y-1 rounded-2xl border border-border bg-card p-4" data-testid="refpol-estimate">
                <EstRow label={t("refpol.est_refund")} val={eur(estimate.refund_amount_cents)} tone="amber" />
                <EstRow label={t("refpol.est_stripe")} val={eur(estimate.stripe_fee_on_charge_cents)} tone="amber" />
                <EstRow label={t("refpol.est_oop")} val={eur(estimate.coach_out_of_pocket_cents)} tone="rose" strong />
                <div className="my-1 h-px bg-border" />
                <EstRow label={t("refpol.est_commission")} val={eur(estimate.platform_commission_kept_cents)} />
                <EstRow label={t("refpol.est_net")} val={`${estimate.coach_net_kept_cents < 0 ? "−" : ""}${eur(Math.abs(estimate.coach_net_kept_cents))}`} tone={estimate.coach_net_kept_cents < 0 ? "rose" : "emerald"} strong />
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("refpol.notes")}</label>
            <Textarea className="min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("refpol.notes_ph")} data-testid="refpol-notes" />
          </div>

          <Button className="w-full gap-2 rounded-full" onClick={save} disabled={saving} data-testid="refpol-save"><Save className="h-4 w-4" /> {saving ? t("common.saving") : t("refpol.save")}</Button>
        </div>
      )}
    </div>
  );
}

function EstRow({ label, val, tone, strong }) {
  const cls = tone === "amber" ? "text-amber-600" : tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-600" : "text-foreground";
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm ${cls} ${strong ? "font-semibold" : ""}`}>{val}</span>
    </div>
  );
}
